import {
  USER_SETTINGS_KEY,
  defaultSettings,
  userSettingsState,
} from '../composables/useUserSettings'

/**
 * Puts the visitor's own article summary choice back on top of the group's
 * default, once the page has hydrated.
 *
 * The server renders the group default, because the choice lives in
 * localStorage and no group key may carry it. Reading it during hydration would
 * not help: Vue reports an attribute mismatch on the card links and declines to
 * repair it, so the server's href would stay in the DOM. Applying it here is a
 * real re-render, and it is a plugin rather than a hook inside the composable
 * because nothing constructs that composable in the browser on a rendered page.
 */
/** The visitor's own choice, or nothing if they never made one. */
function storedChoice(value: unknown): { isEnabled: boolean } | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  if (!('articleSummary' in value)) return undefined
  const summary: unknown = value.articleSummary
  if (typeof summary !== 'object' || summary === null) return undefined
  if (!('isUserSelected' in summary) || summary.isUserSelected !== true) {
    return undefined
  }
  if (!('isEnabled' in summary) || typeof summary.isEnabled !== 'boolean') {
    return undefined
  }
  return { isEnabled: summary.isEnabled }
}

export default defineNuxtPlugin({
  name: 'user-settings',
  setup() {
    let parsed: unknown
    try {
      parsed = JSON.parse(
        window.localStorage.getItem(USER_SETTINGS_KEY) || 'null'
      )
    } catch {
      // A value this app did not write, or a truncated one.
      return
    }
    const choice = storedChoice(parsed)
    if (!choice) return

    const settings = userSettingsState()
    if (!settings.value) settings.value = defaultSettings()

    // Mutated in place, not replaced: useUserSettings hands its callers the
    // articleSummary object itself, so a new wrapper would leave them reading
    // the old one.
    const apply = () => {
      settings.value.articleSummary.isUserSelected = true
      settings.value.articleSummary.isEnabled = choice.isEnabled
    }

    // Only a rendered page has to wait. The client shell renders for the first
    // time after plugins run, so applying the choice now keeps its links right
    // from the first paint, which is what reading localStorage in the
    // composable used to do.
    if (isHydratingServerRender()) {
      onNuxtReady(apply)
      return
    }
    apply()
  },
})
